import { AxiosError, HttpStatusCode } from 'axios';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { QCErrorHTTPCodeEnum } from '90.quickConnect.Models/enums';
import { mapAxiosQCErrorHTTP } from '90.quickConnect.Models/mappings';
import { QCErrorHTTP } from '90.quickConnect.Models/models';
import { BaseResponse } from '40.quickConnect.DataAccess/axios/baseResponse';

const writeLog = (tag: string, errMsg: string, logMethod: string, method?: string) => {
  const message = method ? `${method} method failed: ${errMsg}` : `Méthode incconue: ${errMsg}`;
  const logger = CustomLogger.getInstance();

  switch (logMethod) {
    case 'error':
      logger.error(tag, message);
      break;

    case 'debug':
      logger.debug(tag, message);
      break;

    case 'trace':
      logger.trace(tag, message);
      break;

    case 'info':
      logger.info(tag, message);
      break;

    case 'warn':
      logger.warn(tag, message);
      break;

    case 'log':
      logger.log(tag, message);
  }
};

/**
 * Contexte technique ajoute aux traces d'erreur: le statut HTTP lorsqu'une reponse a ete recue, et
 * le code axios, seule information disponible lorsqu'il n'y en a pas (ERR_NETWORK, ECONNABORTED,
 * ETIMEDOUT, ERR_CANCELED).
 *
 * @param error L'erreur axios remontee par la requete.
 */
const buildErrorContext = (error: AxiosError<any>): string =>
  `Status: ${error?.response?.status ?? 'aucune reponse'} - Code: ${error?.code ?? 'inconnu'}`;

const dispatchErrorResponse = (tag: string, error: AxiosError<any>, levelLog?: string, method?: string) => {
  const qcErrorHttp = mapAxiosQCErrorHTTP.mapFrom(error);

  const url = qcErrorHttp.getUrl();
  const isTimeOut = qcErrorHttp.isTimeout();

  // Un 401 n'est pas une anomalie applicative mais la fin de la session: cookie QC expire ou
  // invalide, utilisateur non authentifie. Il est trace en avertissement quel que soit le niveau
  // demande par l'appelant, afin de ne pas remonter dans les erreurs Application Insights: une
  // seule expiration de session produit un 401 par requete en vol.
  if (qcErrorHttp.getStatus() === HttpStatusCode.Unauthorized) {
    const msg = `Session expiree ou utilisateur non authentifie (401) ${
      url ? `pour l'url: ${url}` : ''
    } description: ${qcErrorHttp.getMessage()}`;
    writeLog(tag, msg, 'warn', method);
    return;
  }

  switch (qcErrorHttp.getCode()) {
    case QCErrorHTTPCodeEnum.ERR_NETWORK: {
      const msg = `Pas de connexion réseau ${url ? "pour l'url: " + url : ''} description: ${qcErrorHttp.getMessage()}`;
      writeLog(tag, msg, 'trace', method);
      break;
    }

    case QCErrorHTTPCodeEnum.ETIMEDOUT: {
      const msg = `Timeout ${url ? "pour l'url: " + url : ''} description: ${qcErrorHttp.getMessage()}`;
      writeLog(tag, msg, 'trace', method);
      break;
    }

    case QCErrorHTTPCodeEnum.ECONNABORTED: {
      if (isTimeOut) {
        const msg = `Timeout ${url ? "pour l'url: " + url : ''} description: ${qcErrorHttp.getMessage()}`;
        writeLog(tag, msg, 'trace', method);
      } else {
        const msg = `Erreur ${url ? "pour l'url: " + url : ''} - ${buildErrorContext(
          error,
        )} description: ${qcErrorHttp.getMessage()}`;
        writeLog(tag, msg, levelLog ?? 'error', method);
      }
      break;
    }

    default: {
      // Toutes les vraies erreurs HTTP (4xx via ERR_BAD_REQUEST, 5xx via ERR_BAD_RESPONSE) arrivent
      // ici. Le statut est alors l'information la plus utile: on ne parle d'erreur inconnue que
      // lorsqu'aucune reponse n'a ete recue, et le code axios prend le relais.
      const httpStatus = error?.response?.status;
      const label = httpStatus !== undefined ? `Erreur HTTP ${httpStatus}` : 'Erreur inconnue';

      const msg = `${label} ${url ? "pour l'url: " + url : ''} - ${buildErrorContext(
        error,
      )} description: ${qcErrorHttp.getMessage()}`;
      writeLog(tag, msg, levelLog ?? 'trace', method);
      break;
    }
  }
};

export const errorHandler = (tag: string, error: unknown, method?: string, level?: string) => {
  const levelLog = level ?? 'trace';
  if (QCErrorHTTP.isHttpError<AxiosError<BaseResponse>>(error)) {
    dispatchErrorResponse(tag, error, level, method);
  } else if (error instanceof Error) {
    const message = method ? `${method} method failed: ${error.message}` : `Méthode incconue: ${error.message}`;
    writeLog(tag, message, levelLog, method);
  }
};
